/**
 * Скролл ПК: один непрерывный RAF в зоне секции.
 * Позиции — applyPcPositions, прогресс — readPcProgress.
 */

import { useEffect } from "react";
import {
    DESKTOP_MQ,
    REDUCE_MQ,
    LERP,
    SETTLE,
} from "./pcScroll/pcScrollConstants";
import readPcProgress from "./pcScroll/readPcProgress";
import applyPcPositions from "./pcScroll/applyPcPositions";

function usePcScrollShowcase({
    trackRef,
    stickyRef,
    pc1Ref,
    pc2Ref,
    pc3Ref,
    pc4Ref,
    latestWorksRef,
}) {
    useEffect(() => {
        const track = trackRef.current;
        const sticky = stickyRef.current;
        const pc1 = pc1Ref.current;
        const pc2 = pc2Ref.current;
        const pc3 = pc3Ref.current;
        const pc4 = pc4Ref.current;
        const latestWorks = latestWorksRef.current;
        if (!track || !sticky || !pc1 || !pc2 || !pc3 || !pc4 || !latestWorks) return;

        const desktopMq = window.matchMedia(DESKTOP_MQ);
        const reduceMq = window.matchMedia(REDUCE_MQ);
        const panels = { pc1, pc2, pc3, pc4, latestWorks };
        const panelList = [pc1, pc2, pc3, pc4, latestWorks];

        let rafId = null;
        let current = 0;
        let lastApplied = -1;
        let willOn = false;
        let inZone = false;
        let active = desktopMq.matches;
        let io = null;

        const setWillChange = (on) => {
            if (willOn === on) return;
            willOn = on;
            const value = on ? "transform" : "";
            panelList.forEach((el) => {
                el.style.willChange = value;
            });
        };

        const paint = (progress) => {
            if (Math.abs(progress - lastApplied) < 0.0003) return;
            lastApplied = progress;
            applyPcPositions(panels, progress);
        };

        const clearInline = () => {
            setWillChange(false);
            lastApplied = -1;
            inZone = false;
            panelList.forEach((el) => {
                el.style.transform = "";
            });
        };

        const targetNow = () => (active ? readPcProgress(track, sticky) : 0);

        const frame = () => {
            if (!active || !inZone) {
                rafId = null;
                return;
            }
            rafId = requestAnimationFrame(frame);

            const target = targetNow();
            if (reduceMq.matches) {
                current = target;
            } else {
                current += (target - current) * LERP;
                if (Math.abs(target - current) < SETTLE) current = target;
            }
            paint(current);
        };

        const startLoop = () => {
            if (!active || rafId != null) return;
            rafId = requestAnimationFrame(frame);
        };

        const stopLoop = () => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = null;
        };

        const resync = () => {
            if (!active || document.hidden) return;
            lastApplied = -1;
            current = targetNow();
            paint(current);
            if (inZone) startLoop();
        };

        const bindDesktop = () => {
            current = targetNow();
            lastApplied = -1;
            paint(current);
            setWillChange(true);

            io = new IntersectionObserver(
                ([entry]) => {
                    inZone = Boolean(entry?.isIntersecting);
                    if (!inZone) {
                        stopLoop();
                        setWillChange(false);
                        return;
                    }
                    setWillChange(true);
                    startLoop();
                },
                { root: null, threshold: 0, rootMargin: "15% 0px" }
            );
            io.observe(track);

            document.addEventListener("visibilitychange", resync);
            window.addEventListener("pageshow", resync);
            startLoop();
        };

        const unbindDesktop = () => {
            stopLoop();
            if (io) {
                io.disconnect();
                io = null;
            }
            document.removeEventListener("visibilitychange", resync);
            window.removeEventListener("pageshow", resync);
            clearInline();
        };

        const onMq = () => {
            const nextActive = desktopMq.matches;
            if (nextActive === active) return;
            active = nextActive;
            current = 0;
            if (active) bindDesktop();
            else unbindDesktop();
        };

        if (active) bindDesktop();
        desktopMq.addEventListener("change", onMq);

        return () => {
            desktopMq.removeEventListener("change", onMq);
            unbindDesktop();
        };
    }, [trackRef, stickyRef, pc1Ref, pc2Ref, pc3Ref, pc4Ref, latestWorksRef]);
}

export default usePcScrollShowcase;
// hooks/usePcScrollShowcase.js
